import * as React from 'react'
import Loader from 'react-loader-spinner'

import apis from '@api/api'
import Logger from '@utils/Logger'
import useI18n from '@store/i18n/useI18n'
import theme from '@theme/Theme'
import styled from '@theme/styled-components'

import ScreenContainer from '@components/screenContainer/screenContainer'
import UserExport from '@screens/admin/content/users'
import DeclarationExport from '@screens/admin/content/declarations'
import Statistics from '@screens/admin/content/statistics'
import Jobs from '@screens/admin/content/jobs'

import { FiChevronRight } from 'react-icons/fi'
import rights from '@utils/RightManager'
import Affectation from './content/affectation'

const user_type_options: Option[] = [
  {
    value: 'INTERNAL',
    label: 'Interne',
  },
  {
    value: 'EXTERNAL',
    label: 'Externe',
  },
  {
    value: 'ALL',
    label: 'Tous les utilisateurs',
  },
]

const MENU_ENTRIES = ['userExport', 'declarationExport', 'statistics'] as const
const MENU_ENTRIES_WITH_JOBS = ['userExport', 'declarationExport', 'statistics', 'jobs', 'affectation'] as const

interface Props {
  navigation: Navigation
}

const AdminData = ({ navigation }: Props) => {
  const i18n = useI18n()
  const [screenStatus, setScreenStatus] = React.useState<'fetched' | 'loading' | 'error'>('loading')
  const [menuSelected, setMenuSelected] = React.useState<typeof MENU_ENTRIES_WITH_JOBS[number]>(MENU_ENTRIES[0])

  const [adminCompanies, setAdminCompanies] = React.useState<Option[]>([])
  const [adminIntUsers, setAdminIntUsers] = React.useState<Option[]>([])
  const [adminExtUsers, setAdminExtUsers] = React.useState<Option[]>([])

  const [adminRegions, setAdminRegions] = React.useState<Option[]>([])

  const menuToDisplay = rights.canCreateUsers() ? MENU_ENTRIES_WITH_JOBS : MENU_ENTRIES

  React.useEffect(() => {
    Promise.all([
      apis.admin.list.internals(),
      apis.admin.list.externals(),
      apis.admin.list.companies(),
      apis.admin.list.regions(),
    ])
      .then(([fetchedInt, fetchedExt, fetchedCompanies, fetchedRegions]) => {
        setAdminIntUsers(fetchedInt.options)
        setAdminExtUsers(fetchedExt.options)
        setAdminCompanies(fetchedCompanies.companies.map((c) => ({ label: c.libelle, value: c.id })))
        setAdminRegions(fetchedRegions.regions.map((r) => ({ label: r.libelle, value: r.id })))
        setScreenStatus('fetched')
      })
      .catch((err) => {
        Logger.info('ERROR FETCHING DATA', err)
        setScreenStatus('error')
      })
  }, [])

  const Content = () => {
    switch (menuSelected) {
      case 'userExport':
        return <UserExport user_type_options={user_type_options} />
      case 'declarationExport':
        return (
          <DeclarationExport
            adminCompanies={adminCompanies}
            adminIntUsers={adminIntUsers}
            adminExtUsers={adminExtUsers}
          />
        )
      case 'statistics':
        return <Statistics adminRegions={adminRegions} user_type_options={user_type_options} />
      case 'jobs':
        return <Jobs navigation={navigation} />
      case 'affectation':
        return <Affectation navigation={navigation} />
      default:
        return null
    }
  }

  return (
    <ScreenContainer
      menu={
        <LeftContainer>
          <ScreenTitleContainer>
            <ScreenTitle>{i18n.t('components.adminData.title')}</ScreenTitle>
            <Back onClick={() => navigation.push('/')}>{i18n.t('components.menu.back')}</Back>
          </ScreenTitleContainer>
          <MenuOptions>
            {menuToDisplay.map((itm, idx) => (
              <MenuItem key={idx} selected={itm === menuSelected} onClick={() => setMenuSelected(itm)}>
                <MenuItemLibelle selected={itm === menuSelected}>
                  {i18n.t(`components.adminData.sectionTitles.${itm}`)}
                </MenuItemLibelle>
                <FiChevronRight size={30} color={theme.colors.dataMenuColor} />
              </MenuItem>
            ))}
          </MenuOptions>
        </LeftContainer>
      }
      content={
        screenStatus === 'loading' ? (
          <StatusContainer>
            <Loader type="ThreeDots" color={theme.colors.classicButtonLoader} height={30} />
          </StatusContainer>
        ) : screenStatus === 'error' ? (
          <ErrorLibelle>{i18n.t('components.adminCompanies.error')}</ErrorLibelle>
        ) : (
          <RightContainer>
            <Content />
          </RightContainer>
        )
      }
    />
  )
}
export default AdminData

const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const LeftContainer = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 10px 0 10px;
  overflow: hidden;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  margin: 0 10px 15px 10px;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`

const RightContainer = styled('div')`
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 50px;
  justify-content: flex-start;
  overflow: scroll;
`

const MenuOptions = styled('div')`
  display: flex;
  flex: 1;
  overflow: scroll;
  margin-bottom: 25px;
  flex-direction: column;
`
const MenuItem = styled('div')<{ selected: boolean }>`
  ${(props) => `border: 1px solid ${props.theme.colors.dataMenuColor};`}
  ${(props) => props.selected && `background-color: ${props.theme.colors.dataChoiceBackground};`}
  justify-content: space-between;
  margin: 25px 15px 0 15px;
  border-radius: 10px;
  align-items: center;
  flex-direction: row;
  min-height: 75px;
  padding: 0 25px;
  cursor: pointer;
  display: flex;
`
const MenuItemLibelle = styled('div')<{ selected: boolean }>`
  ${(props) => props.theme.fonts.label};
  ${(props) => props.selected && 'font-weight: bold;'}
  ${(props) => `color: ${props.theme.colors.dataMenuColor};`}
`
